import MapObject from './map_object';
import BusStop from './bus_stop';
import DiningLocation from './dining_location';
import GenderNeutralRestroom from './gender_neutral_restroom';
import Library from './library';
import Bcycle from './bcycle';
import Dialog from '../lib/dialog';
import MapMenu from '../lib/map_menu';
import UiInteraction from '../lib/ui_interaction';

export default class MapView {
	/**
	 * Represents a MapView linked to the server API.
	 * @constructor
	 * @param {object} data - The data object to persist to the Rails MapView.
	 */
	constructor(data) {
		this.data = {};
		if (data) {
			this.data = data;
			this.url = W.api_root + 'map_views' + '/' + this.data.token;
		}
	}

	// Restore this map state on a map
	restore() {
		W.map
			.setZoom(this.data.zoom - 1) // Mapbox zoom is 1-off from our legacy leaflet zoom
			.setCenter(this.data.center.reverse());

		// support legacy map_views that saved map_object_keys
		if (this.data.map_object_keys && !this.data.popups) {
			this.data.map_object_keys.forEach((map_object_key) => {
				const popupKeyParts = map_object_key.split('_');

				if (popupKeyParts[0] === 'MapObject') {
					const map_object = new MapObject(popupKeyParts[1]);
					UiInteraction.fetchAndAddFeature(map_object);
				} else if (popupKeyParts[0] === 'BusStop') {
					const bus_stop = new BusStop(popupKeyParts[1]);
					UiInteraction.fetchAndAddFeature(bus_stop);
				}
			});
		}

		// Re-enable the layers
		if (this.data.layer_keys) {
			this.data.layer_keys.forEach((layer_name) => {
				if (W.layers[layer_name]) {
					const layer = W.layers[layer_name];

					// When this view is added, don't have setView()
					// change the map view the first time
					layer.ignoreNextSetView = true;
					layer.add(W.map);
					layer.checkboxEl.setAttribute('checked', 'checked');
				}
			});
		}

		// Re-add the popups
		if (this.data.popups) {
			this.data.popups.forEach((popupObject) => {
				// After switching to JS fetch to save popups
				// the popups get saved to the database object without
				// the zero key. This adds backward compatability.
				const popup = Object.keys(popupObject)[0] === '0' ? popupObject[0] : popupObject;
				const popupKeyParts = popup.key.split('_');
				const popupObjectType = popupKeyParts[0];
				let popupObjectId = popupKeyParts[1];

				if ('DiningLocation' === popupObjectType) {
					let dining_location = new DiningLocation(popupObjectId);
					UiInteraction.fetchAndAddFeature(dining_location);
				}

				if ('BusStop' === popupObjectType) {
					const bus_stop = new BusStop(popupObjectId);
					UiInteraction.fetchAndAddFeature(bus_stop);
				}

				if ('GenderNeutralRestroom' === popupObjectType) {
					const gender_neutral_restroom = new GenderNeutralRestroom(popupObjectId);
					UiInteraction.fetchAndAddFeature(gender_neutral_restroom);
				}

				if ('Library' === popupObjectType) {
					const library = new Library(popupObjectId);
					UiInteraction.fetchAndAddFeature(library);
				}

				if ('MapObject' === popupObjectType) {
					const map_object = new MapObject(popupObjectId);
					UiInteraction.fetchAndAddFeature(map_object);
				}

				if ('Bcycle' === popupObjectType) {
					// Bcycyle IDs look like bcycle_madison_1886
					// we need to piece it back together
					popupKeyParts.shift();
					popupObjectId = popupKeyParts.join('_');

					const bcycle = new Bcycle(popupObjectId);
					UiInteraction.fetchAndAddFeature(bcycle);
				}
			});
		}
	}

	// Show all visible layers
	visibleLayers() {
		let out = [];
		Object.entries(W.layers).forEach((entry) => {
			let name = entry[0];
			let layer = entry[1];
			if (layer && layer.visible) {
				out.push(name);
			}
		});
		return out;
	}

	// Save various state from the map and layer, and set attributes
	fetchState() {
		this.data.center = this.mapCenter();
		this.data.zoom = this.mapZoom() + 1;
		this.data.layer_keys = this.visibleLayers();
		this.data.popups = W.popups;
		this.data.map_mode = 'map';
	}

	mapCenter() {
		const center = W.map.getCenter();
		return [center.lat, center.lng];
	}

	mapZoom() {
		return W.map.getZoom();
	}

	async save() {
		this.fetchState();
		const request = new Request(W.api_root + 'map_views', {
			method: 'POST',
			body: JSON.stringify({ map_view: this.data }),
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
		});

		try {
			const response = await fetch(request);
			const result = await response.json();

			// this returns a map_view json object from the server
			// it token value is used to load the Map URL dialog content
			return result;
		} catch (error) {
			console.error('Error:', error);
		}
	}

	static async create() {
		const map_view = new MapView();
		return await map_view.save();
	}

	static addListeners() {
		document.getElementById('save_url').addEventListener('click', () => {
			// Save the map state to server
			MapView.create().then((map_view) => {
				// Load the dialog
				const url = W.api_root + 'map_views/' + map_view.token + '.html';
				Dialog.load(url).then((dialog) => {
					dialog.classList.add('fixed-height');
					dialog.setAttribute('aria-labelledby', 'dialog-label');
					const urlInput = document.getElementById('map_view_path');
					setTimeout(() => {
						// why????
						urlInput.select();
					}, 5);
				});
				MapMenu.hide();
			});
		});
	}
}
